import gsap from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";
import Lenis from "lenis";

/**
 * Shared plumbing for the supplier-one-new layout's scroll choreography
 * (ported from the safari template's useSafariMotion.ts).
 *
 * `main.scss` gives `body` a height of 100vh and `overflow-x: hidden`, which
 * forces the computed `overflow-y` to `auto` and makes <body> the scroll
 * container everywhere else in the app. This layout moves that role one level
 * down to its own `.supplier-root` wrapper instead, because Lenis silently
 * coerces a `wrapper` of `document.body` or `document.documentElement` back
 * to `window` — there is no way to point it at body directly. Anything driven
 * by scroll position (ScrollTrigger, an IntersectionObserver's `root`) has to
 * go through `scrollerEl()` or it will watch a window that never scrolls.
 */

let registered = false;

/** Registers ScrollTrigger once per client session. */
export function useGsap() {
  if (!registered && typeof window !== "undefined") {
    gsap.registerPlugin(ScrollTrigger);
    registered = true;
  }
  return gsap;
}

export function prefersReducedMotion() {
  if (typeof window === "undefined") return true;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/**
 * The nearest scrollable ancestor of `el`, or null when nothing between it
 * and the root scrolls (the browser's own way of saying "use the viewport").
 */
export function getSupplierScrollRoot(el: HTMLElement): Element | null {
  let node = el.parentElement;
  while (node && node !== document.documentElement) {
    const overflowY = getComputedStyle(node).overflowY;
    if (overflowY === "auto" || overflowY === "scroll") return node;
    node = node.parentElement;
  }
  return null;
}

/** This layout's scroll container — see the getSupplierScrollRoot doc above. */
export function supplierRootEl(): HTMLElement | null {
  if (typeof document === "undefined") return null;
  return document.querySelector<HTMLElement>(".supplier-root");
}

/**
 * The element that actually scrolls. Resolved from a node inside the page so
 * it keeps working if the layout ever moves the overflow to a wrapper;
 * returns undefined when nothing between the node and the root scrolls, which
 * is ScrollTrigger's way of saying "use the viewport".
 *
 * With no `from`, resolves `.supplier-root` directly rather than walking up
 * from it: `getSupplierScrollRoot` only examines an element's *ancestors*, and
 * `.supplier-root` computing its own overflow doesn't make it its own
 * ancestor.
 */
export function scrollerEl(from?: HTMLElement | null): Element | undefined {
  if (typeof document === "undefined") return undefined;
  if (!from) return supplierRootEl() ?? undefined;
  return getSupplierScrollRoot(from) ?? undefined;
}

/**
 * ScrollTrigger config with the scroller already filled in. Spread it into a
 * trigger rather than restating `scroller` at every call site.
 */
export function trigger(
  config: ScrollTrigger.Vars,
  from?: HTMLElement | null,
): ScrollTrigger.Vars {
  return { scroller: scrollerEl(from), ...config };
}

/** Current scroll offset of the real scroll container. */
export function scrollOffset(): number {
  const el = scrollerEl();
  if (el) return el.scrollTop;
  return typeof window === "undefined" ? 0 : window.scrollY;
}

/**
 * Scrolls an in-page target into view, routed through Lenis when it's
 * running so a jump doesn't fight the smooth-scroll loop (a native
 * `scrollTo` mid-Lenis-animation gets overridden on the next tick). Falls
 * back to the container's native smooth scrolling.
 */
export function scrollToTarget(target: Element, offset = 0, immediate = false) {
  const lenis = getSupplierLenis();
  if (lenis) {
    lenis.scrollTo(target as HTMLElement, { offset: -offset, immediate });
    return;
  }

  const container = scrollerEl();
  const behavior: ScrollBehavior = immediate ? "auto" : "smooth";

  if (container) {
    const top =
      target.getBoundingClientRect().top -
      container.getBoundingClientRect().top +
      container.scrollTop -
      offset;
    container.scrollTo({ top, behavior });
    return;
  }

  window.scrollTo({
    top: target.getBoundingClientRect().top + window.scrollY - offset,
    behavior,
  });
}

/* ---------------------------------------------------------------------
   LENIS — smooth scroll over `.supplier-root`

   A single instance, owned by the layout (see layouts/supplierOneNew.vue).
   Without it, wheel/trackpad input scrolls the container natively (a hard
   step function) rather than with momentum/ease. GSAP ticks from the same
   `gsap.ticker` callback, with ScrollTrigger updated synchronously off
   Lenis's own `scroll` event rather than the browser's, so any
   scroll-tied effect (the hero's image drift, the footer's reveal) reads a
   single source of truth per frame instead of two racing.
   --------------------------------------------------------------------- */

let lenis: Lenis | null = null;
let lenisTicker: ((time: number) => void) | null = null;

export function initSupplierLenis(): Lenis | null {
  if (typeof window === "undefined") return null;
  if (lenis) return lenis;
  if (prefersReducedMotion()) return null;

  // Not document.body: Lenis silently coerces a wrapper of body or
  // documentElement back to window (see the getSupplierScrollRoot doc).
  // `.supplier-root` is the layout's own root div carrying the overflow
  // instead.
  const wrapper = supplierRootEl();
  if (!wrapper) return null;

  // `.supplier-content` (see layouts/supplierOneNew.vue), not
  // `wrapper.firstElementChild`: Lenis's own Dimensions watches wrapper +
  // content with a ResizeObserver to recompute its scroll limit, each off
  // their own box size. `.supplier-root`'s box is a fixed 100vh — it never
  // resizes when the page inside it grows — and the header (the old
  // firstElementChild stand-in) doesn't resize when later content does
  // either. `.supplier-content` wraps everything that can grow the page in
  // one plain div with auto height, so its own box reliably changes size
  // exactly when the scrollable content does (the same class of bug fixed
  // for the safari template's scroll-stuck issue — see useSafariMotion.ts).
  const content =
    wrapper.querySelector<HTMLElement>(".supplier-content") ?? wrapper;

  const gsap = useGsap();

  lenis = new Lenis({
    wrapper,
    content,
    duration: 1.05,
    smoothWheel: true,
    touchMultiplier: 1.1,
  });

  lenis.on("scroll", () => ScrollTrigger.update());

  lenisTicker = (time: number) => lenis?.raf(time * 1000);
  gsap.ticker.add(lenisTicker);

  return lenis;
}

export function getSupplierLenis(): Lenis | null {
  return lenis;
}

export function destroySupplierLenis() {
  if (lenisTicker) {
    useGsap().ticker.remove(lenisTicker);
    lenisTicker = null;
  }
  lenis?.destroy();
  lenis = null;
}
